import { useNavigate, useParams } from "react-router";
import { Camera, Gift, History as HistoryIcon, Home, type LucideIcon } from "lucide-react";
import { useLanguage } from "@/i18n/language";

export type KarigarNavKey = "home" | "scan" | "history" | "redeem";

interface NavItem {
  key: KarigarNavKey;
  icon: LucideIcon;
  labelHi: string;
  labelEn: string;
  /** Path under `/:orgSlug/karigar`. Absent for tabs that only move the page. */
  path?: string;
}

const ITEMS: readonly NavItem[] = [
  { key: "home", icon: Home, labelHi: "होम", labelEn: "Home", path: "" },
  { key: "scan", icon: Camera, labelHi: "स्कैन", labelEn: "Scan", path: "/scan" },
  { key: "history", icon: HistoryIcon, labelHi: "इतिहास", labelEn: "History", path: "/history" },
  { key: "redeem", icon: Gift, labelHi: "QCoin रिडीम करें", labelEn: "Redeem QCoin", path: "/redeem" },
];

/**
 * Fixed bottom tab bar for the karigar app — WIREFRAMES.md "Karigar home".
 * Labels follow the karigar's chosen language (Hindi until they pick).
 *
 * Four tabs, matching the predecessor app the karigars already know: Home,
 * Scan, History, Redeem. The active tab sits in a brand-tinted pill, so where
 * you are reads from shape as well as colour — this is used outdoors, often in
 * bright light, on inexpensive screens.
 *
 * `KarigarShell` pairs this with matching bottom padding on the page content
 * so the fixed bar never covers the last row.
 */
export function KarigarBottomNav({ active }: { active: KarigarNavKey }) {
  const navigate = useNavigate();
  const { t } = useLanguage("hi");
  const { orgSlug } = useParams<{ orgSlug: string }>();

  function activate(item: NavItem): void {
    if (!orgSlug) return;

    navigate(`/${orgSlug}/karigar${item.path ?? ""}`);
  }

  return (
    <nav
      aria-label={t("कारीगर नेविगेशन", "Karigar navigation")}
      className="fixed inset-x-0 bottom-0 z-20 border-t border-rule bg-raised/95 pb-[env(safe-area-inset-bottom)] shadow-[0_-8px_24px_-12px_rgb(0_0_0/0.18)] backdrop-blur-md"
    >
      <div className="mx-auto flex max-w-md items-stretch justify-between px-1">
        {ITEMS.map((item) => {
          const isActive = item.key === active;
          const Icon = item.icon;
          return (
            <button
              aria-current={isActive ? "page" : undefined}
              className="group flex flex-1 flex-col items-center gap-1 px-1 pt-2 pb-2 text-center"
              key={item.key}
              onClick={() => activate(item)}
              type="button"
            >
              <span
                className={[
                  "grid h-8 w-14 place-items-center rounded-full transition-colors",
                  isActive ? "bg-brand-light text-brand-ink" : "text-muted group-hover:bg-ink/[0.05] group-hover:text-ink",
                ].join(" ")}
              >
                <Icon aria-hidden="true" className="size-5" strokeWidth={isActive ? 2.25 : 1.85} />
              </span>
              <span
                className={`text-xs leading-tight ${isActive ? "font-semibold text-ink" : "font-medium text-muted"}`}
              >
                {t(item.labelHi, item.labelEn)}
              </span>
            </button>
          );
        })}
      </div>
    </nav>
  );
}
